import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnInit,
} from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { forkJoin, of } from "rxjs";
import { catchError } from "rxjs/operators";
import { MaterialModule } from "src/app/app.materials";

import { ConstraintSetterComponent } from "../constraint-setter/constraint-setter.component";
import { PortfolioService } from "src/app/pages/portfolio/portfolio.service";
import { UtilService } from "src/app/shared/services/util.service";

export interface EditConstraintsDialogData {
  portfolioId: number;
}

@Component({
  selector: "app-edit-constraints-dialog",
  templateUrl: "./edit-constraints-dialog.component.html",
  standalone: true,
  imports: [MaterialModule, ConstraintSetterComponent],
  // OnPush isolates the dialog from the parent's CD cycle: forkJoin completion
  // fires inside the same Zone tick as an XHR response, and with default CD
  // the resulting `loading` flip can race with the parent's check-no-changes
  // pass and throw NG0100, halting the template update mid-render.
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditConstraintsDialogComponent implements OnInit {
  portfolio: any;
  constraints: any[] = [];
  sectors: any[] = [];
  countries: any[] = [];
  assets: any[] = [];
  loading = true;
  error: string | null = null;

  constructor(
    public dialogRef: MatDialogRef<EditConstraintsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EditConstraintsDialogData,
    private portfolioService: PortfolioService,
    private util: UtilService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.loadData();
  }

  private loadData(): void {
    forkJoin({
      portfolio: this.portfolioService.viewItem(this.data.portfolioId),
      optimization: this.portfolioService
        .fetchOptimizationData(this.data.portfolioId, true)
        .pipe(catchError(() => of(null))),
    }).subscribe({
      next: (result: any) => {
        this.portfolio = result.portfolio;
        this.constraints = result.optimization?.group_constraints || [];
        this.initConstraintLists();
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.error = "Failed to load portfolio data";
        this.loading = false;
        this.cdr.markForCheck();
      },
    });
  }

  private initConstraintLists(): void {
    if (!this.portfolio?.securities) return;

    const sectors = [];
    const countries = [];

    this.portfolio.securities.forEach((c: any) => {
      if (c.security_type_equity === "SHARE") {
        if (c.sector) sectors.push(c.sector);
        if (c.country) countries.push(c.country);
      }
    });

    this.sectors = this.util.keepUniqueObjectOfArray(sectors);
    this.countries = this.util.keepUniqueObjectOfArray(countries);
    const securityAssets = this.portfolio.securities.map((s: any) => ({
      ...s,
      name: s.company_name,
    }));
    // Sub-portfolios live in security_overviews (not securities), so merge them
    // in or they'd be missing from the constraint asset selector.
    this.assets = this.mergeAssetsWithSubPortfolios(securityAssets).sort((a, b) =>
      a.name?.toLowerCase() > b.name?.toLowerCase() ? 1 : -1,
    );
  }

  /**
   * Appends sub-portfolio "assets" (sourced from security_overviews, where they
   * appear with security_type_equity === 'PORTFOLIO') to the given asset list,
   * deduped by id. Sub-portfolios are not present in portfolio.securities.
   */
  private mergeAssetsWithSubPortfolios(assets: any[]): any[] {
    const overviews = this.portfolio?.security_overviews || {};
    const existingIds = new Set(assets.map((a: any) => String(a.id)));
    const subPortfolios = Object.keys(overviews)
      .filter(
        (ticker: string) =>
          overviews[ticker]?.security_type_equity === "PORTFOLIO",
      )
      .map((ticker: string) => {
        const o = overviews[ticker];
        // Keep the PF_<id> ticker as the constraint identifier; the backend
        // resolves sub-portfolio holdings by this ticker, not by numeric id.
        return {
          ...o,
          id: ticker,
          name: o.company_name || o.name || ticker,
          security_type_equity: "PORTFOLIO",
        };
      })
      .filter((sub: any) => !existingIds.has(String(sub.id)));
    return [...assets, ...subPortfolios];
  }

  onOptimize(): void {
    this.dialogRef.close(true);
  }

  onClose(): void {
    this.dialogRef.close(false);
  }
}
